import React, { useEffect, useState } from "react"
import { MdOpenInNew, MdOutlineDelete, MdOutlineInbox, MdRefresh } from "react-icons/md"
import { useParams } from "react-router-dom"
import { toast } from "react-toastify"
import api from "../../services/api"
import Button from "../Form/Button"
import Input from "../Form/Input"
import Select from "../Form/Select"
import Modal from "./index"
import ModalConfirm from "./ModalConfirm"
import styles from "../../pages/ApiIntegration/apiIntegration.module.css"

const statusOptions = [
    { id: "all", value: "Todos os status" },
    { id: "pending_config", value: "Aguardando configuração" },
    { id: "processing", value: "Processando" },
    { id: "imported", value: "Importado" },
    { id: "failed", value: "Falhou" },
    { id: "discarded", value: "Descartado" },
]

const statusLabels = {
    pending_config: "Aguardando configuração",
    processing: "Processando",
    imported: "Importado",
    failed: "Falhou",
    discarded: "Descartado",
}

const formatDateTime = date => date ? new Date(date).toLocaleString("pt-BR") : "-"
const getFormName = form => form?.form_metadata?.name || form?.form_metadata?.id || form?.form_metadata?.page_title || "Cadastros do formulário"

const findLeadValue = (lead = {}, patterns = []) => {
    const entry = Object.entries(lead).find(([key, value]) => value && patterns.some(pattern => key.toLowerCase().includes(pattern)))
    return entry?.[1]
}

const getSubmissionIdentity = (lead = {}) => {
    const name = findLeadValue(lead, ["name", "nome"])
    const email = findLeadValue(lead, ["email", "e-mail"])
    const phone = findLeadValue(lead, ["phone", "telefone", "celular", "whatsapp"])
    const fallback = Object.values(lead).find(value => value !== undefined && value !== null && value !== "")
    return { title: name || email || phone || fallback || "Cadastro sem identificação", detail: name ? (email || phone) : (email && phone ? phone : "") }
}

const KeyValueList = ({ values = {} }) => {
    const entries = Object.entries(values).filter(([, value]) => value !== undefined && value !== null && value !== "")
    if (!entries.length) return <div className={styles.submissionEmptySection}>Nenhuma informação recebida.</div>
    return (
        <dl className={styles.submissionKeyValueList}>
            {entries.map(([key, value]) => (
                <div key={key}>
                    <dt>{key}</dt>
                    <dd>{typeof value === "object" ? JSON.stringify(value) : String(value)}</dd>
                </div>
            ))}
        </dl>
    )
}

const ModalApiFormSubmissions = ({ form = {}, closeModal = () => {}, onChanged = () => {} }) => {
    const { account_id } = useParams()
    const [submissions, setSubmissions] = useState([])
    const [pagination, setPagination] = useState({})
    const [status, setStatus] = useState("all")
    const [search, setSearch] = useState("")
    const [appliedSearch, setAppliedSearch] = useState("")
    const [selected, setSelected] = useState(null)
    const [loading, setLoading] = useState(false)
    const [actionLoading, setActionLoading] = useState(false)
    const [submissionToDiscard, setSubmissionToDiscard] = useState(null)

    const getSubmissions = async (page = 1, append = false) => {
        try {
            setLoading(true)
            const response = await api.get(`/integrations/api/forms/${form._id}/submissions`, {
                params: { status, search: appliedSearch, page, limit: 25 }
            })
            const data = response?.data?.data || []
            setSubmissions(previous => append ? [...previous, ...data] : data)
            setPagination(response?.data?.pagination || {})
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível carregar os cadastros.")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        setSelected(null)
        getSubmissions()
    }, [status, appliedSearch])

    const openSubmission = async submissionId => {
        try {
            setLoading(true)
            const response = await api.get(`/integrations/api/forms/${form._id}/submissions/${submissionId}`)
            setSelected(response?.data?.data || null)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível abrir o cadastro.")
        } finally {
            setLoading(false)
        }
    }

    const refreshAfterAction = async submission => {
        await getSubmissions()
        await openSubmission(submission._id)
        onChanged()
    }

    const discardSubmission = async () => {
        if (!submissionToDiscard) return
        const submissionId = submissionToDiscard._id
        setSubmissionToDiscard(null)
        try {
            setActionLoading(true)
            const response = await api.patch(`/integrations/api/forms/${form._id}/submissions/${submissionId}/discard`)
            toast.success(response?.data?.message)
            await refreshAfterAction(response?.data?.data)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível descartar o cadastro.")
        } finally {
            setActionLoading(false)
        }
    }

    const retrySubmission = async () => {
        if (!selected) return
        try {
            setActionLoading(true)
            const response = await api.post(`/integrations/api/forms/${form._id}/submissions/${selected._id}/retry`)
            toast.success(response?.data?.message)
            await refreshAfterAction(response?.data?.data)
        } catch (err) {
            const updated = err?.response?.data?.data
            if (updated) await refreshAfterAction(updated)
            toast.error(err?.response?.data?.message || "Não foi possível reprocessar o cadastro.")
        } finally {
            setActionLoading(false)
        }
    }

    const applySearch = () => setAppliedSearch(search.trim())

    return (
        <>
            <Modal
            icon={<MdOutlineInbox />}
            title="Cadastros recebidos"
            description={`${getFormName(form)}${form.url ? ` • ${form.url}` : ""}`}
            closeModal={closeModal}
            fullWidith={true}
        >
            <div className={styles.submissionsLayout}>
                <section className={styles.submissionsListPanel}>
                    <div className={`${styles.submissionsToolbar} ${styles.apiSubmissionsToolbar}`}>
                        <div className={styles.submissionsSearch}>
                            <Input type="search" placeholder="Buscar nome, e-mail, telefone..." value={search} onChange={event => setSearch(event.target.value)} onKeyDown={event => event.key === "Enter" && applySearch()} />
                            <Button variant="secondary" size="sm" fullWidth={false} onClick={applySearch}>Buscar</Button>
                        </div>
                        <Select name="Status" placeholder="Filtrar status" options={statusOptions} selected={status} multiple={false} allowDeselectAll={false} onChange={setStatus} />
                    </div>
                    <div className={styles.submissionsCount}>{Number(pagination.total || 0).toLocaleString("pt-BR")} cadastros encontrados</div>
                    <div className={styles.submissionsList}>
                        {submissions.map(submission => {
                            const identity = getSubmissionIdentity(submission.lead)
                            return (
                                <button key={submission._id} type="button" className={`${styles.submissionListItem} ${selected?._id === submission._id ? styles.active : ""}`} onClick={() => openSubmission(submission._id)}>
                                    <div>
                                        <strong>{identity.title}</strong>
                                        {identity.detail && <span>{identity.detail}</span>}
                                        <small>{formatDateTime(submission.created_at)}</small>
                                    </div>
                                    <span className={`${styles.submissionStatus} ${styles[submission.status]}`}>{statusLabels[submission.status] || submission.status}</span>
                                </button>
                            )
                        })}
                        {!submissions.length && !loading && <div className={styles.submissionsEmpty}>Nenhum cadastro encontrado com esses filtros.</div>}
                        {loading && <div className={styles.submissionsEmpty}>Carregando cadastros...</div>}
                    </div>
                    {pagination.has_more && <Button variant="secondary" onClick={() => getSubmissions(Number(pagination.page || 1) + 1, true)} disabled={loading}>Carregar mais</Button>}
                </section>
                <section className={styles.submissionDetailPanel}>
                    {!selected && <div className={styles.submissionDetailEmpty}><MdOutlineInbox /><strong>Selecione um cadastro</strong><span>Veja os valores recebidos em cada campo e seu status de importação.</span></div>}
                    {selected && (
                        <>
                            <div className={styles.submissionDetailHeader}>
                                <div>
                                    <span className={`${styles.submissionStatus} ${styles[selected.status]}`}>{statusLabels[selected.status] || selected.status}</span>
                                    <small>Recebido em {formatDateTime(selected.created_at)}</small>
                                </div>
                                <div className={styles.submissionActions}>
                                    {selected.status === "imported" && selected.lead_id && <a className={styles.submissionLeadLink} href={`/${account_id}/crm?openModal=1&leadId=${selected.lead_id}`} target="_blank" rel="noreferrer">Abrir lead <MdOpenInNew /></a>}
                                    {selected.status === "failed" && <Button variant="secondary" size="sm" fullWidth={false} onClick={retrySubmission} disabled={actionLoading}><MdRefresh /> Tentar novamente</Button>}
                                    {["pending_config", "failed"].includes(selected.status) && <Button variant="danger" size="sm" fullWidth={false} onClick={() => setSubmissionToDiscard(selected)} disabled={actionLoading}>Descartar</Button>}
                                </div>
                            </div>
                            {selected.error_message && <div className={styles.submissionError}><strong>Falha na importação</strong><span>{selected.error_message}</span></div>}
                            <div className={styles.submissionSection}>
                                <h3>Dados do formulário</h3>
                                <KeyValueList values={selected.lead} />
                            </div>
                            <div className={styles.submissionSection}>
                                <h3>Origem e campanha</h3>
                                <KeyValueList values={selected.utm} />
                            </div>
                            <details className={styles.submissionTechnical}>
                                <summary>Dados técnicos</summary>
                                <KeyValueList values={{ ...selected.browser, ...selected.tracking, ...selected.consent, url: selected.url }} />
                            </details>
                        </>
                    )}
                </section>
            </div>
            </Modal>
            {submissionToDiscard &&
                <ModalConfirm
                    icon={<MdOutlineDelete />}
                    title="Descartar cadastro?"
                    description="Este cadastro não será enviado ao Kanban."
                    text="Os dados continuarão disponíveis no histórico com o status Descartado."
                    confirmLabel="Descartar"
                    confirmVariant="danger"
                    closeModal={() => setSubmissionToDiscard(null)}
                    onSubmit={discardSubmission}
                />
            }
        </>
    )
}

export default ModalApiFormSubmissions
